<template>
  <div class="drawer">
    <div class="drawer-container">
      <div class="drawer-content with-footer">
        <el-form
          ref="orderForm"
          :model="order"
          label-width="80px"
          size="small"
          class="box"
        >
          <fieldset>
            <legend>
              {{ $t("text.baseInfo") }}
              <el-checkbox v-model="order.hurry" class="float-right">{{
                $t("order.hurry")
              }}</el-checkbox>
            </legend>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.serialNo')"
                  :prop="'serialNo'"
                  :rules="[
                    {
                      required: true,
                      validator: validateSerialNo,
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-input
                    size="small"
                    v-model.trim="order.serialNo"
                    :placeholder="$t('prompt.alphabetNumberLine')"
                    clearable
                    style="width: 100%"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.innerSerialNo')"
                  :prop="'innerSerialNo'"
                >
                  <el-input
                    size="small"
                    v-model.trim="order.innerSerialNo"
                    clearable
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.dealSuggest')"
                  :prop="'dealSuggest'"
                >
                  <el-input v-model.trim="order.dealSuggest"></el-input>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="8">
                <el-form-item :label="$t('text.handOverAt')">
                  <el-date-picker
                    v-model="order.handOverAt"
                    type="date"
                    style="width: 100%"
                    :picker-options="pickerTodayAfterTime"
                  ></el-date-picker>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('text.createAt')"
                  :prop="'createAt'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('text.createAt')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-date-picker
                    v-model="order.createAt"
                    type="date"
                    style="width: 100%"
                    :picker-options="pickerOptionsFiveDays"
                  ></el-date-picker>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
          <fieldset>
            <legend>
              {{ $t("order.productInfo") }}
            </legend>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item
                  :label="$t('product.productNo')"
                  :prop="'productNo'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('product.productNo')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-autocomplete
                    v-model.trim="order.productNo"
                    :fetch-suggestions="querySearchProductNo"
                    :trigger-on-focus="false"
                    style="width: 100%"
                    @blur="blurProductNo(order)"
                    @select="handleSelectProductNo"
                  >
                    <template slot-scope="{ item }">
                      <div class="name">{{ item.productNo }}</div>
                      <span class="addr">{{ item.productNoAlias }}</span>
                    </template>
                  </el-autocomplete>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.customerProductNo')"
                  :prop="'customerProductNo'"
                >
                  <el-input
                    size="small"
                    v-model.trim="order.customerProductNo"
                    clearable
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('product.category')"
                  :prop="'categoryId'"
                >
                  <el-select size="small" v-model="order.categoryId" clearable>
                    <el-option
                      v-for="category in categories"
                      :key="category.id"
                      :label="category.name"
                      :value="category.id"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="16">
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item :label="$t('product.size')" :prop="'size'">
                      <el-autocomplete
                        size="small"
                        v-model.trim="order.size"
                        :fetch-suggestions="querySearchSize"
                        style="width: 100%"
                        @select="handleSelectSize"
                      />
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item :label="$t('product.color')" :prop="'color'">
                      <el-autocomplete
                        size="small"
                        v-model.trim="order.color"
                        :fetch-suggestions="querySearchColor"
                        style="width: 100%"
                        @select="handleSelectColor"
                      />
                    </el-form-item>
                  </el-col>
                </el-row>
                <el-row :gutter="20">
                  <el-col :span="12">
                    <el-form-item
                      :label="$t('order.price')"
                      :prop="'price'"
                      :rules="[
                        {
                          required: true,
                          message: $t('rules.required', [$t('order.price')]),
                          trigger: 'blur',
                        },
                      ]"
                    >
                      <el-input
                        size="small"
                        v-model.trim="order.price"
                        :placeholder="currencySymbol"
                        @blur="blurPrice"
                      >
                        <div slot="append">
                          售 {{ currencySymbol }}{{ order.productPrice || 0 }}
                        </div>
                      </el-input>
                    </el-form-item>
                  </el-col>
                  <el-col :span="12">
                    <el-form-item
                      :label="$t('order.discountRate')"
                      :prop="'discountRate'"
                    >
                      <el-input
                        size="small"
                        v-model.trim="order.discountRate"
                        @blur="blurDiscountRate"
                      >
                        <div slot="append">%</div>
                      </el-input>
                    </el-form-item>
                  </el-col>
                </el-row>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.colorImage')"
                  :prop="'colorImage'"
                >
                  <upload-single-picture v-model="order.colorImage" />
                </el-form-item>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="8">
                <el-form-item
                  :label="$t('text.quantity')"
                  :prop="'quantity'"
                  :rules="[
                    {
                      required: true,
                      message: $t('rules.required', [$t('text.quantity')]),
                      trigger: 'blur',
                    },
                  ]"
                >
                  <el-row>
                    <el-col :span="14">
                      <el-input
                        size="small"
                        v-model.trim="order.quantity"
                      ></el-input>
                    </el-col>
                    <el-col :span="10">
                      <el-select size="small" v-model="order.unit" clearable>
                        <el-option
                          v-for="unit in units"
                          :key="unit"
                          :label="unit"
                          :value="unit"
                        ></el-option>
                      </el-select>
                    </el-col>
                  </el-row>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item
                  :label="$t('order.extraCharge')"
                  :prop="'extraCharge'"
                >
                  <el-input
                    size="small"
                    v-model.trim="order.extraCharge"
                  ></el-input>
                </el-form-item>
              </el-col>
              <el-col :span="8">
                <el-form-item :label="$t('order.subtotal')">
                  {{ currencySymbol }}
                  {{ order.price * order.quantity + Number(order.extraCharge) }}
                </el-form-item>
              </el-col>
            </el-row>
            <el-row>
              <el-col :span="8">
                <el-form-item :label="$t('menu.brands')" :prop="'quantity'">
                  <el-select
                    size="small"
                    v-model="order.productBrand"
                    clearable
                    filterable
                  >
                    <el-option
                      v-for="brand in brands"
                      :key="brand.id"
                      :label="brand.name"
                      :value="brand.name"
                    ></el-option>
                  </el-select>
                </el-form-item>
              </el-col>
            </el-row>
          </fieldset>
          <fieldset>
            <legend>{{ $t("product.specJson") }}</legend>

            <el-row :gutter="20" class="mgb10">
              <el-col
                v-for="attribute in productNoAttributes"
                :key="attribute.name"
                :span="6"
                class="mgb10"
              >
                <el-input size="small" v-model.trim="attribute.value">
                  <template slot="prepend">
                    <div>
                      <raw-html-limit-number
                        :content="attribute.name"
                        :number="6"
                      />
                    </div>
                  </template>
                </el-input>
              </el-col>
            </el-row>
            <el-row :gutter="20">
              <el-col :span="16">
                <fieldset class="mgb15">
                  <legend>
                    {{ $t("product.images") }}
                    <svg-icon icon-class="link" @click="linkImage" />
                  </legend>
                  <dropzone v-model="order.images" multiple />
                </fieldset>
              </el-col>
              <el-col :span="8">
                <fieldset class="mgb15">
                  <legend>{{ $t("product.attachment") }}</legend>
                  <div class="mgb10">
                    <small class="orange">{{
                      $t("product.attachmentExplain")
                    }}</small>
                  </div>
                  <attachment v-model="order.attachment" />
                </fieldset>
              </el-col>
            </el-row>
            <fieldset class="mgb15">
              <legend>{{ $t("text.remark") }}</legend>
              <tinymce-editor
                ref="editor"
                v-model="order.remark"
                :height="300"
              ></tinymce-editor>
            </fieldset>
          </fieldset>
        </el-form>
      </div>
    </div>
    <div class="drawer-footer">
      <el-button
        type="primary"
        plain
        :loading="saveDoing"
        size="small"
        @click="saveSubmit(false)"
        >{{ $t("text.submit") }}</el-button
      >
      <el-button
        v-if="Permit.canActionsChargeEdition('orderConfirm')"
        type="success"
        plain
        :loading="saveDoing"
        size="small"
        @click="saveSubmit(true)"
        >{{ $t("action.submitAndConfirm") }}</el-button
      >
    </div>
    <el-dialog
      :append-to-body="true"
      :title="$t('action.linkImage')"
      :visible.sync="linkImageDF"
      :close-on-click-modal="false"
      width="50%"
    >
      <el-form ref="linkImageForm" :model="linkImageObj">
        <el-form-item
          :prop="'linkImageStr'"
          :rules="[
            {
              required: true,
              message: $t('rules.required', [$t('action.linkImage')]),
              trigger: 'blur',
            },
          ]"
        >
          <el-input
            v-model="linkImageObj.linkImageStr"
            :placeholder="$t('prompt.linkImageExplain')"
            type="textarea"
            :autosize="{ minRows: 4, maxRows: 10 }"
          />
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button
          type="primary"
          plain
          :loading="linkImageDoing"
          size="small"
          @click="linkImageSubmit"
          >{{ $t("text.submit") }}</el-button
        >
        <el-button size="small" @click="linkImageDF = false">{{
          $t("text.close")
        }}</el-button>
      </div>
    </el-dialog>
  </div>
</template>
<script>
import axios from "axios";
import Vue from "vue";
import ls from "store2";
import Util from "@/utils/index.js";
import { page } from "@/utils/mixins.js";

import UploadSinglePicture from "@/components/UploadSinglePicture";
import Dropzone from "@/components/Dropzone";
import Attachment from "@/components/Attachment";
import TinymceEditor from "@/components/TinymceEditor";
import BackButton from "@/components/BackButton";

//
import Order from "@/api/order.js";
import Bargain from "@/api/bargain.js";
import Category from "@/api/category.js";
import Size from "@/api/size.js";
import Enumeration from "@/api/enumeration.js";
import Color from "@/api/color.js";
import Quotation from "@/api/quotation.js";
import Product from "@/api/product.js";
import Brand from "@/api/brand.js";

export default {
  props: {
    uuid: String,
  },
  mixins: [page],
  components: {
    UploadSinglePicture,
    Dropzone,
    Attachment,
    TinymceEditor,
    BackButton,
  },
  data() {
    var validateSerialNo = (rule, value, callback) => {
      const self = this;
      if (value === null || value === "") {
        callback(
          new Error(self.$t("rules.required", [self.$t("order.serialNo")]))
        );
        return;
      }
      if (!Util.regexAlphabetNumberLine(value)) {
        callback(new Error(self.$t("prompt.regexAlphabetNumberLine")));
        return;
      }
      Bargain.findBySerialNo({ serialNo: value }).then(function (res) {
        if (res.data.code === 200) {
          callback(
            new Error(self.$t("rules.exist", [self.$t("bargain.serialNo")]))
          );
        } else {
          Order.findBySerialNo({ serialNo: value }).then(function (res) {
            if (res.data.code === 200) {
              callback(
                new Error(self.$t("rules.exist", [self.$t("order.serialNo")]))
              );
            } else {
              callback();
            }
          });
        }
      });
      // Order.findBySerialNo({ serialNo: value }).then(function (res) {
      //   if (res.data.code === 200 && res.data.entity.id !== self.order.id) {
      //     callback(
      //       new Error(self.$t("rules.exist", [self.$t("order.serialNo")]))
      //     );
      //   } else {
      //     callback();
      //   }
      // });
    };
    return {
      pickerTodayAfterTime: {
        disabledDate(time) {
          return time.getTime() < Date.now() - 8.64e7;
        },
      },
      pickerOptionsFiveDays: {
        disabledDate(time) {
          const curDate = new Date().getTime();
          const day = 5 * 24 * 3600 * 1000;
          const threeMonths = curDate - day;
          return time.getTime() > Date.now() || time.getTime() < threeMonths;
        },
      },
      validateSerialNo: validateSerialNo,
      bargain: null,
      categories: [],
      sizes: [],
      units: [],
      colors: [],
      attributes: [],
      brands: [],
      order: {
        serialNo: null,
        innerSerialNo: null,
        handOverAt: null,
        hurry: false,
        currency: null,
        settlementId: null,
        bargainId: null,
        productNo: null,
        customerProductNo: null,
        categoryId: null,
        categoryName: null,
        color: null,
        colorImage: null,
        size: null,
        quantity: null,
        productPrice: null,
        price: null,
        unit: null,
        extraCharge: null,
        discountRate: null,
        images: null,
        specJson: null,
        craftwork: null,
        attachment: null,
        remark: null,
        dealSuggest: null,
        confirmAt: null,
        type: null,
        createAt: null,
        _oldSizeExplain: null,
        _productIntroduction: null,
        productBrand: null,
      },
      synProduct: false,
      synProductExistDF: false,
      ignoreDF: false,
      saveDoing: false,
      cnyRate: null,
      currencySymbol: null,
      productNoColors: [],
      productNoSizes: [],
      productNoAttributes: [],
      products: [],
      categorySizes: [],
      //
      productSizePrices: [],
      productWholesalePrice: null,
      productSalesPrice: null,
      productSalesCurrency: null,
      productSalesCnyRate: null,
      productSalesCurrencySymbol: null,
      quotationPrice: null,
      quotationSizePrices: [],
      CNYPrice: null,
      // 图片链接
      linkImageDF: false,
      linkImageObj: {
        linkImageStr: null,
      },
      linkImageDoing: false,
      //
      preProductNo: null,
    };
  },

  computed: {},
  created() {
    const self = this;
    self.order.createAt = new Date();
    axios
      .all([
        Bargain.findByUuid({ uuid: self.uuid }),
        Category.findByTenantId(),
        Size.findByTenantId(),
        Enumeration.findByType("unit"),
        Color.findByTenantId(),
        Enumeration.findByType("attribute"),
        Brand.findByTenantId(),
      ])
      .then(
        axios.spread(function (
          bargain,
          categories,
          sizes,
          units,
          colors,
          attributes,
          brands
        ) {
          if (bargain.status === 200) {
            self.bargain = bargain.data.entity;
            self.order.bargainId = self.bargain.id;
            self.order.serialNo =
              self.bargain.serialNo +
              "-" +
              Number(self.bargain.orders.length + 1);
            if (self.bargain.settleType === "now") {
              self.order.settlementId = 0;
            }
            // console.log(
            //   self.bargain.settleType,
            //   self.bargain.settleType === "now",
            //   "===",
            //   self.order.settlementId
            // );
            // 币种
            self.order.currency = self.bargain.currency;
            const currentCurrencies = ls.session("CURRENCIES");
            const currencyObj = currentCurrencies.find(
              (c) => c.code === self.bargain.currency
            );
            if (currencyObj != null) {
              self.cnyRate = currencyObj.cnyRate;
              self.currencySymbol = currencyObj.symbol;
            } else {
              self.cnyRate = 1;
              self.currencySymbol = self.bargain.currency;
            }
          } else {
            self.sysError();
          }
          if (categories.status === 200) {
            self.categories = categories.data;
          } else {
            self.sysError();
          }
          if (sizes.status === 200) {
            self.sizes = sizes.data;
          } else {
            self.sysError();
          }
          if (units.data.code === 200) {
            self.units = JSON.parse(units.data.entity.itemsJson);
            self.order.unit = self.units.length != 0 ? self.units[0] : null;
          } else {
            self.sysError();
          }
          if (colors.status === 200) {
            self.colors = colors.data;
          } else {
            self.sysError();
          }
          if (attributes.data.code === 200) {
            self.attributes = JSON.parse(attributes.data.entity.itemsJson);
          } else {
            self.sysError();
          }
          if (brands.status === 200) {
            self.brands = brands.data;
            self.order.productBrand = ls.session("productBrand");
          } else {
            self.sysError();
          }
        })
      );
  },

  methods: {
    // 图片链接
    linkImage() {
      this.linkImageDF = true;
      this.linkImageObj.linkImageStr = null;
    },
    linkImageSubmit() {
      const self = this;
      this.$refs.linkImageForm.validate((valid) => {
        if (valid) {
          const linkImageArr = this.linkImageObj.linkImageStr.split("\n");
          const linkImageArrFilter = linkImageArr.filter((a) => a != null);
          if (self.order.images == null) {
            self.order.images = linkImageArrFilter.join(";");
          } else {
            self.order.images =
              self.order.images + ";" + linkImageArrFilter.join(";");
          }
          this.linkImageDF = false;
        } else {
          return false;
        }
      });
    },
    blurPrice() {
      if (
        this.order.productPrice != null &&
        this.order.productPrice !== 0 &&
        this.order.price != null
      ) {
        this.order.discountRate = Util.toFixed(
          (this.order.price / this.order.productPrice) * 100,
          3
        );
      }
    },
    blurDiscountRate() {
      if (
        this.order.discountRate !== null &&
        !Util.regexQuantity(this.order.discountRate)
      ) {
        this.$message({
          message: this.$t("rules.regex", [this.$t("order.discountRate")]),
          type: "warning",
        });
        return;
      }
      if (this.order.productPrice != null) {
        this.order.price =
          this.order.productPrice * (this.order.discountRate / 100);
      }
    },
    // 货号
    querySearchProductNo(queryString, cb) {
      const self = this;
      //
      self.clearProduct();
      //
      if (queryString == null || queryString.length < 1) {
        return;
      }
      Product.vagueFindByProductNo({ productNo: queryString }).then(function (
        res
      ) {
        self.products = res.data;
        if (self.products.length !== 0) {
          for (const p of self.products) {
            Vue.set(p, "value", p.productNo);
          }
        }
        cb(self.products);
      });
    },
    handleSelectProductNo(item) {
      const self = this;
      self.order.productNo = item.productNo;
      self.blurProductNo(item);
      self.preProductNo = item.productNo;
    },
    blurProductNo(item) {
      const self = this;
      // console.log("====", item.productNo, self.preProductNo != item.productNo);
      // if (item.productNo != null && self.preProductNo != item.productNo) {
      if (item.productNo != null) {
        Product.findByProductNo({
          productNo: item.productNo,
        }).then(function (res) {
          if (res.data.code === 200) {
            const item = res.data.entity;
            self.order.customerProductNo = item.productNoAlias;
            self.order.categoryId = item.categoryId;
            self.order.categoryName = item.categoryName;
            self.selsectCategory();
            self.order.discountRate = null;
            self.order.color = null;
            self.order.colorImage = null;
            self.order.size = null;
            self.order.unit = item.unit;
            self.order.images = item.images;
            self.order.craftwork = item.craftwork;
            if (self.order.remark.includes(item.introduction)) {
              self.order.remark = item.remark;
            } else {
              self.order.remark =
                (self.order.remark || "") + (item.introduction || "");
            }
            self.order._productIntroduction = item.introduction;
            self.productNoColors = item.colors;
            self.productNoSizes = item.sizes;
            self.order.productBrand = item.brand;

            if (
              item.customAttributeJson != null &&
              item.customAttributeJson !== "[]"
            ) {
              self.productNoAttributes = JSON.parse(
                item.customAttributeJson
              ).map((att) => {
                return { value: null, name: att };
              });
            }
            self.productSizePrices = item.productSizePrices;
            self.productWholesalePrice = item.wholesalePrice;
            self.productSalesPrice = item.salesPrice;
            self.productSalesCurrency = item.salesCurrency;
            // 产品已存在，禁止同步到产品中心
            self.synProduct = false;
            self.synProductExistDF = true;
          } else {
            self.clearProduct();
            // 产品不存在，可选择同步到产品中心
            self.synProduct = false;
            self.synProductExistDF = false;
          }
          self.getQuotationPrice();
        });
      }
    },
    clearProduct() {
      const self = this;
      self.order.customerProductNo = null;
      self.order.categoryId = null;
      self.order.categoryName = null;
      self.selsectCategory();
      self.order.discountRate = null;
      self.order.color = null;
      self.order.colorImage = null;
      self.order.size = null;
      self.order.unit = self.units.length != null ? self.units[0] : null;
      self.order.images = null;
      self.order.craftwork = null;
      self.order.remark = self.order.remark || "";
      self.order._productIntroduction = null;

      self.order._disablePrice = false;
      self.order._disableDiscountRate = false;
      self.order.discountRate = null;
      self.order.productPrice = null;
      self.order.price = null;
      self.order.productBrand = null;
      //
      self.productNoColors = self.colors.map((c) => c.name);
      self.productNoSizes = self.sizes.map((s) => s.name);
      self.productNoAttributes = self.attributes.map((att) => {
        return { value: null, name: att };
      });
      //
      self.productSizePrices = [];
      self.productWholesalePrice = null;
      self.productSalesPrice = null;
      self.productSalesCnyRate = null;
      self.productSalesCurrencySymbol = null;
      self.productSalesCurrency = null;
      self.quotationPrice = null;
      self.quotationSizePrices = [];
    },
    getQuotationPrice() {
      const self = this;
      if (self.order.productNo != null && self.order.productNo != "") {
        Quotation.findByCustomerIdAndProductNo(self.bargain.customerId, {
          productNo: self.order.productNo,
        }).then(function (res) {
          if (res.data.code === 200) {
            self.quotationPrice = res.data.entity.price;
            self.quotationSizePrices = res.data.entity.quotationSizePrices;
          } else {
            self.quotationPrice = null;
            self.quotationSizePrices = [];
          }
          self.computeCNYPrice();
        });
      }
    },
    computeCNYPrice() {
      const self = this;
      // 产品对客户的规格报价
      if (
        self.quotationSizePrices.length != 0 &&
        self.quotationSizePrices.find((p) => p.size == self.order.size) !=
          null &&
        self.quotationSizePrices.find((p) => p.size == self.order.size).price !=
          null
      ) {
        self.CNYPrice = self.quotationSizePrices.find(
          (p) => p.size == self.order.size
        ).price;
        self.computeRatePrice();
        return;
      }
      // 产品对客户的报价
      if (self.quotationPrice !== null) {
        self.CNYPrice = self.quotationPrice;
        self.computeRatePrice();
        return;
      }
      // 产品规格批发价格
      if (
        self.bargain.customerType === "wholesale" &&
        self.productSizePrices.length != 0 &&
        self.productSizePrices.find((s) => s.size === this.order.size) &&
        self.productSizePrices.find((s) => s.size === this.order.size)
          .wholesalePrice != null
      ) {
        self.CNYPrice = self.productSizePrices.find(
          (s) => s.size === this.order.size
        ).wholesalePrice;
        self.computeRatePrice();
        return;
      }
      // 产品批发价格
      if (
        self.bargain.customerType === "wholesale" &&
        self.productWholesalePrice != null &&
        self.productWholesalePrice != 0
      ) {
        self.CNYPrice = self.productWholesalePrice;
        self.computeRatePrice();
        return;
      }

      // 产品销售价币种
      const currentCurrencies = ls.session("CURRENCIES");
      const currencyObj = currentCurrencies.find(
        (c) => c.code === self.productSalesCurrency
      );
      if (currencyObj != null) {
        self.productSalesCnyRate = currencyObj.cnyRate;
        self.productSalesCurrencySymbol = currencyObj.symbol;
      } else {
        self.productSalesCnyRate = 1;
        self.productSalesCurrencySymbol = self.productSalesCurrency;
      }
      // 产品规格售价
      if (
        self.productSizePrices.length != 0 &&
        self.productSizePrices.find((s) => s.size === this.order.size) &&
        self.productSizePrices.find((s) => s.size === this.order.size)
          .salesPrice != null
      ) {
        self.CNYPrice =
          self.productSizePrices.find((s) => s.size === this.order.size)
            .salesPrice * self.productSalesCnyRate;
        self.computeRatePrice();
        return;
      }
      // 产品售价
      self.CNYPrice = self.productSalesPrice * self.productSalesCnyRate;
      self.computeRatePrice();
      return;
    },
    computeRatePrice() {
      const self = this;
      if (self.CNYPrice != null) {
        self.order.productPrice =
          Math.floor((self.CNYPrice / self.cnyRate) * 100) / 100;
        self.order.price =
          Math.floor((self.CNYPrice / self.cnyRate) * 100) / 100;
      } else {
        self.order.productPrice = null;
        self.order.price = null;
      }
    },
    //
    selsectCategory() {
      const self = this;
      if (self.order.categoryId === "") {
        self.order.categoryId = null;
      }
      self.categorySizes = [];
      if (self.order.categoryId != null && self.order.categoryId !== "") {
        const selsectCategoryObj = self.categories.find(
          (c) => c.id === self.order.categoryId
        );
        if (
          selsectCategoryObj &&
          selsectCategoryObj.sizes != null &&
          selsectCategoryObj.sizes.length !== 0
        ) {
          self.categorySizes = selsectCategoryObj.sizes;
        }
      }
    },
    // 颜色
    querySearchColor(queryString, cb) {
      const colors =
        this.productNoColors.length !== 0
          ? this.productNoColors.map((pc) => {
              return { value: pc };
            })
          : this.colors.map((c) => {
              return { value: c.name };
            });
      if (queryString == null) {
        cb(colors);
      } else {
        cb(colors.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectColor() {
      this.order.color = Util.jyTrim(this.order.color);
      //
      this.order.colorImage = null;
      const colorObj = this.colors.find((c) => c.name === this.order.color);
      if (colorObj != null && colorObj.image != null) {
        this.order.colorImage = colorObj.image;
      }
    },
    // 规格
    querySearchSize(queryString, cb) {
      const self = this;
      let sizes = [];
      if (self.productNoSizes.length !== 0) {
        sizes = this.productNoSizes.map((pc) => {
          return { value: pc };
        });
      } else {
        if (self.categorySizes.length !== 0) {
          sizes = this.categorySizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        } else {
          sizes = this.sizes.map((c) => {
            return { value: c.name, explain: c.explain };
          });
        }
      }
      if (queryString == null) {
        cb(sizes);
      } else {
        cb(sizes.filter((c) => c.value.indexOf(queryString) !== -1));
      }
    },
    handleSelectSize() {
      const self = this;
      self.order.size = Util.jyTrim(self.order.size);
      self.computeRemark();
      self.computeCNYPrice();
    },
    computeRemark() {
      const self = this;
      const sizeObj = self.sizes.find((s) => s.name === this.order.size);
      // 替换已经有的尺码说明
      if (self.order.remark != null && self.order._oldSizeExplain != null) {
        self.order.remark = self.order.remark.replace(
          ";【" + self.order._oldSizeExplain + "】",
          ""
        );
        self.order.remark = self.order.remark.replace(
          "<br/>【" + self.order._oldSizeExplain + "】",
          ""
        );
        self.order.remark = self.order.remark.replace(
          "【" + self.order._oldSizeExplain + "】",
          ""
        );
        self.order.remark = self.order.remark.replace(
          self.order._oldSizeExplain,
          ""
        );
      }
      // 加入新的尺码说明
      if (sizeObj != null && sizeObj.explain != null && sizeObj.explain != "") {
        if (this.order.remark !== null && self.order.remark != "") {
          self.order.remark =
            self.order.remark + "<br/>【" + sizeObj.explain + "】";
        } else {
          this.order.remark = "【" + sizeObj.explain + "】";
        }
        self.order._oldSizeExplain = sizeObj.explain;
      }
    },

    //
    saveSubmit(confirm) {
      let self = this;
      self.$refs.orderForm.validate((valid) => {
        if (valid) {
          if (!Util.regexPrice(self.order.price)) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("order.price")]),
              type: "warning",
            });
            return;
          }
          if (
            !Util.regexQuantity(self.order.quantity) ||
            self.order.quantity === "0"
          ) {
            self.$message({
              message: self.$t("rules.regex", [self.$t("text.quantity")]),
              type: "warning",
            });
            return;
          }
          self.order.color = Util.jyTrim(self.order.color);
          self.order.size = Util.jyTrim(self.order.size);
          if (self.order.categoryId !== null) {
            self.order.categoryName = self.order.categoryName =
              self.categories.find((s) => s.id === self.order.categoryId).name;
          }
          const attributesArr =
            self.productNoAttributes.length !== 0
              ? self.productNoAttributes.filter((sp) => sp.value != null)
              : [];
          self.order.specJson = JSON.stringify(attributesArr);
          if (
            self.order.productPrice == null ||
            self.order.productPrice === "0" ||
            self.order.productPrice == 0
          ) {
            self.order.productPrice = self.order.price;
          }
          self.order.createAt = Util.formatDate(self.order.createAt);
          if (confirm) {
            self.order.confirmAt = Util.formatDate(new Date());
          } else {
            self.order.confirmAt = null;
          }
          // console.log("=====222", self.order);
          self.saveDoing = true;
          Order.create(self.order).then(function (res) {
            if (res.status === 200) {
              self.$message({
                message: self.$t("prompt.saveSuccess"),
                type: "success",
              });
              self.$emit("updateInfo");
              self.$eventHub.$emit("orderNew");
            } else {
              self.sysError();
            }
            self.saveDoing = false;
          });
        } else {
          return false;
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
</style>